import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { isNil, isUndefined } from 'lodash-es';
import { combineLatest, map, Observable, of } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { DataProviderIdEnum } from '../../../../enums/data-provider';
import { MainTabBasePathEnum } from '../../../../enums/main-tabs';
import * as fromPropertyProfileActions from '../../../../store/actions/property-profile.actions';
import * as fromDataProviderSelectors from '../../../../store/selectors/data-provider.selectors';
import * as fromOfficeSelectors from '../../../../store/selectors/office.selectors';
import * as fromOrganizationSelectors from '../../../../store/selectors/organization.selectors';
import * as fromPropertyProfileSelectors from '../../../../store/selectors/property-profile.selectors';
import { GenericComponent } from '../../../components/generic/generic.component';

interface FiltersFormGroup {
  isDetailsAndCharacteristicsVisible: FormControl<boolean>;
  isPlatMapVisible: FormControl<boolean>;
  isSaleInformationVisible: FormControl<boolean>;
  isSchoolsVisible: FormControl<boolean>;
  isTransactionHistoryVisible: FormControl<boolean>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-report',
  standalone: false,
  templateUrl: './report.page.html'
})
export class ReportPageComponent extends GenericComponent {
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);

  profileId: string | null;

  filterForm: FormGroup<FiltersFormGroup> = new FormGroup<FiltersFormGroup>({
    isDetailsAndCharacteristicsVisible: new FormControl(true, { nonNullable: true }),
    isPlatMapVisible: new FormControl(true, { nonNullable: true }),
    isSaleInformationVisible: new FormControl(true, { nonNullable: true }),
    isSchoolsVisible: new FormControl(true, { nonNullable: true }),
    isTransactionHistoryVisible: new FormControl(true, { nonNullable: true })
  });

  showSaleInformation$: Observable<boolean>;
  showSchools$: Observable<boolean>;
  showTransactionHistory$: Observable<boolean>;

  showPlatMap$: Observable<boolean>;

  defaultBackHref: string | undefined;
  defaultHref: string;

  constructor() {
    super();

    this.profileId = this.activatedRoute.snapshot.paramMap.get('id');

    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? MainTabBasePathEnum.PropertySearch;
    this.defaultHref = [this.defaultBackHref ?? '', 'profile', this.profileId].join('/');

    this.showSaleInformation$ = this.store.pipe(select(fromOrganizationSelectors.selectSaleInformationEnabled));

    this.showSchools$ = combineLatest([
      of(this.profileId).pipe(
        filter((id) => !isNil(id)),
        concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileHasSchools(id)))),
        filter((hasSchoolsData) => !isUndefined(hasSchoolsData))
      ),
      this.store.pipe(select(fromOfficeSelectors.selectSchoolDistrictInfo))
    ]).pipe(map(([hasSchoolsData, schoolsEnabled]) => hasSchoolsData && schoolsEnabled));

    this.showTransactionHistory$ = this.store.pipe(select(fromOrganizationSelectors.selectTransactionHistoryEnabled));

    this.showPlatMap$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) =>
        combineLatest([
          this.store.pipe(select(fromDataProviderSelectors.selectId)),
          this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileIsPlatMapAvailable(id)))
        ])
      ),
      map(([providerId, isPlatMapAvailable]) => providerId === DataProviderIdEnum.BlackKnight && isPlatMapAvailable)
    );
  }

  ionViewWillEnter() {
    this.addUniqueSubscription(
      'updateAvailableSectionsFormValues',
      combineLatest([
        this.showSaleInformation$,
        this.showSchools$,
        this.showTransactionHistory$,
        this.showPlatMap$
      ]).subscribe(([isSaleInformationVisible, isSchoolsVisible, isTransactionHistoryVisible, isPlatMapVisible]) => {
        this.filterForm.patchValue({
          isPlatMapVisible: isPlatMapVisible,
          isSaleInformationVisible: isSaleInformationVisible,
          isSchoolsVisible: isSchoolsVisible,
          isTransactionHistoryVisible: isTransactionHistoryVisible
        });
      })
    );

    this.addUniqueSubscription(
      'updateReportExportOptions',
      combineLatest([of(this.profileId).pipe(filter((id) => !isNil(id))), this.filterForm.valueChanges]).subscribe(
        ([profileId, filterFormValues]) => {
          this.store.dispatch(
            fromPropertyProfileActions.updateReportExportOptions({
              payload: {
                isReportExportDetailsAndCharacteristicsVisible: filterFormValues.isDetailsAndCharacteristicsVisible,
                isReportExportPlatMapVisible: filterFormValues.isPlatMapVisible,
                isReportExportSaleInformationVisible: filterFormValues.isSaleInformationVisible,
                isReportExportSchoolsVisible: filterFormValues.isSchoolsVisible,
                isReportExportTransactionHistoryVisible: filterFormValues.isTransactionHistoryVisible,
                reportId: profileId
              }
            })
          );
        }
      )
    );

    this.addUniqueSubscription(
      'updateReportExportForm',
      of(this.profileId)
        .pipe(
          filter((id) => !isNil(id)),
          concatMap((id) =>
            this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileExportOptions(id)))
          ),
          filter((options) => !isUndefined(options))
        )
        .subscribe((options) => {
          this.filterForm.patchValue(
            {
              ...options
            },
            { emitEvent: false }
          );
          this.filterForm.updateValueAndValidity({ emitEvent: false });
        })
    );
  }

  clearFilters() {
    if (this.profileId) {
      this.store.dispatch(
        fromPropertyProfileActions.resetReportOptions({
          payload: {
            reportId: this.profileId
          }
        })
      );
    }
  }
}
